import type { SupabaseClient } from '@supabase/supabase-js';

export type TerminalTransportState = 'connecting' | 'live' | 'reconnecting' | 'fallback';

export interface TerminalOutputPayload {
  readonly sessionId: string;
  readonly seq: number;
  readonly chunk: string;
  readonly status?: string;
}

export interface OwnerTerminalInputPayload {
  readonly sessionId: string;
  readonly data: string;
  readonly source: 'owner_terminal';
  readonly inputId: string;
}

export interface TerminalInputResultPayload {
  readonly sessionId: string;
  readonly inputId: string;
  readonly status: 'delivered' | 'not_delivered';
  readonly reason?: string;
}

/**
 * Payload for direct owner keyboard/paste input only.
 *
 * Automated runtime turns use the node's persistent runtime adapter and must not reuse
 * this browser transport or its input assumptions.
 */
export function ownerTerminalInputPayload(sessionId: string, data: string, inputId: string): OwnerTerminalInputPayload {
  return { sessionId, data, source: 'owner_terminal', inputId };
}

export function terminalInputResultPayload(value: unknown): TerminalInputResultPayload | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  if (typeof row['sessionId'] !== 'string' || typeof row['inputId'] !== 'string') return null;
  if (row['status'] !== 'delivered' && row['status'] !== 'not_delivered') return null;
  const reason = typeof row['reason'] === 'string' ? row['reason'] : undefined;
  return {
    sessionId: row['sessionId'],
    inputId: row['inputId'],
    status: row['status'],
    ...(reason === undefined ? {} : { reason }),
  };
}

export interface SequencedOutput {
  readonly seq: number;
  readonly chunk: string;
}

/** Parse the node's terminal-output broadcast without dropping its ordering key. */
export function terminalOutputPayload(value: unknown): TerminalOutputPayload | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  if (typeof row['sessionId'] !== 'string' || typeof row['chunk'] !== 'string') return null;
  const seq = row['seq'];
  if (typeof seq !== 'number' || !Number.isSafeInteger(seq) || seq <= 0) return null;
  const status = typeof row['status'] === 'string' ? row['status'] : undefined;
  return { sessionId: row['sessionId'], seq, chunk: row['chunk'], ...(status === undefined ? {} : { status }) };
}

/**
 * One high-water mark for both Realtime and database replay.
 *
 * Realtime can arrive ahead of a missing database row. Hold that later chunk until the
 * missing sequence arrives; if database catch-up includes a chunk already seen live, the
 * pending map makes it a no-op. Every sequence is therefore rendered at most once.
 */
export class TerminalSequenceBuffer {
  private highWater = 0;
  private readonly pending = new Map<number, string>();

  get highestRenderedSeq(): number { return this.highWater; }
  get hasGap(): boolean { return this.pending.size > 0 && !this.pending.has(this.highWater + 1); }

  reset(): void {
    this.highWater = 0;
    this.pending.clear();
  }

  /**
   * #805: attach at the tail instead of replaying a session from seq 1. A busy Worker
   * terminal holds hundreds of thousands of repaint chunks; replaying them 500 at a time
   * was the heaviest load on the database. History before `seq` is never drawn.
   */
  startAt(seq: number): void {
    if (!Number.isSafeInteger(seq) || seq <= 1 || this.highWater > 0) return;
    this.highWater = seq - 1;
    for (const key of [...this.pending.keys()]) if (key <= this.highWater) this.pending.delete(key);
  }

  accept(seq: number, chunk: string): readonly SequencedOutput[] {
    if (!Number.isSafeInteger(seq) || seq <= this.highWater || seq <= 0) return [];
    if (!this.pending.has(seq)) this.pending.set(seq, chunk);
    return this.drain();
  }

  acceptMany(rows: readonly SequencedOutput[]): readonly SequencedOutput[] {
    for (const row of [...rows].sort((a, b) => a.seq - b.seq)) {
      if (Number.isSafeInteger(row.seq) && row.seq > this.highWater && row.seq > 0 && !this.pending.has(row.seq)) {
        this.pending.set(row.seq, row.chunk);
      }
    }
    return this.drain();
  }

  private drain(): readonly SequencedOutput[] {
    const ready: SequencedOutput[] = [];
    while (this.pending.has(this.highWater + 1)) {
      const seq = this.highWater + 1;
      const chunk = this.pending.get(seq)!;
      this.pending.delete(seq);
      this.highWater = seq;
      ready.push({ seq, chunk });
    }
    return ready;
  }
}

/** Bounded exponential reconnect delay: 0.5s, 1s, 2s, 4s, then 8s max. */
export function terminalReconnectDelay(attempt: number): number {
  const safeAttempt = Math.max(0, Math.min(30, Math.floor(attempt)));
  return Math.min(8_000, 500 * (2 ** safeAttempt));
}

export type TerminalProcessText = 'Terminal not open' | 'Opening' | 'Running' | 'Waiting' | 'Exited';

/** Owner-facing PTY/process truth, deliberately separate from the network transport. */
export function terminalProcessText(
  hasTerminal: boolean,
  status: string | null,
  closedAt: string | null = null,
): TerminalProcessText {
  if (!hasTerminal) return 'Terminal not open';
  if (closedAt !== null || status === 'closed' || status === 'exited' || status === 'failed') return 'Exited';
  if (status === 'idle' || status === 'waiting_for_input' || status === 'waiting_for_approval') return 'Waiting';
  if (status === 'running' || status === 'closing') return 'Running';
  return 'Opening';
}

/**
 * Only the calm transport states are owner-facing. The internal initial `connecting`
 * phase already has database fallback available, so it must never look like a terminal
 * that is itself disconnected.
 */
export function terminalTransportText(hasTerminal: boolean, state: TerminalTransportState): string | null {
  if (!hasTerminal) return null;
  if (state === 'live') return 'Live';
  if (state === 'reconnecting') return 'Reconnecting';
  return 'Secure fallback';
}

export type TerminalTransportEvent = 'connect' | 'subscribed' | 'lost' | 'fallback_ready' | 'retry' | 'auth_refreshed';

export function nextTerminalTransport(
  current: TerminalTransportState,
  event: TerminalTransportEvent,
): TerminalTransportState {
  if (event === 'auth_refreshed') return current;
  if (event === 'subscribed') return 'live';
  if (event === 'lost' || event === 'fallback_ready') return 'fallback';
  if (event === 'retry') return 'reconnecting';
  return 'connecting';
}

/**
 * Transport churn and terminal absence never clear the visible scrollback.
 * A reset is reserved for attaching to a concrete session id different from the one whose
 * output is currently on screen.
 */
export function terminalSessionChanged(previous: string | null, next: string | null): boolean {
  return next !== null && previous !== next;
}

export type TerminalRealtimeBroadcastEvent = 'terminal-output' | 'terminal-state' | 'terminal-input-result' | 'persistent-ai-state'
  | 'ai-execution-actual';

export interface TerminalRealtimeSubscriber {
  readonly onTransport?: (state: TerminalTransportState, reconnectAttempt: number) => void;
  readonly onBroadcast?: (event: TerminalRealtimeBroadcastEvent, payload: unknown) => void;
}

export interface TerminalRealtimeLease {
  /** True means Realtime acknowledged the broadcast. False means fallback is now active. */
  send(event: string, payload: unknown): Promise<boolean>;
  /** Ask the shared bounded reconnect loop to recover; never bypasses its backoff. */
  recover(): void;
  release(): void;
}

type RealtimeChannel = ReturnType<SupabaseClient['channel']>;

interface AuthBridge {
  refs: number;
  readonly unsubscribe: () => void;
}

/** One auth listener per Supabase client, regardless of how many Workers are mounted. */
const authBridges = new WeakMap<SupabaseClient, AuthBridge>();

function retainRealtimeAuth(client: SupabaseClient): () => void {
  const existing = authBridges.get(client);
  if (existing) {
    existing.refs += 1;
    return () => releaseRealtimeAuth(client);
  }
  const { data } = client.auth.onAuthStateChange((_event, authSession) => {
    if (authSession?.access_token) void client.realtime.setAuth(authSession.access_token);
  });
  authBridges.set(client, {
    refs: 1,
    unsubscribe: () => data.subscription.unsubscribe(),
  });
  return () => releaseRealtimeAuth(client);
}

function releaseRealtimeAuth(client: SupabaseClient): void {
  const bridge = authBridges.get(client);
  if (!bridge) return;
  bridge.refs -= 1;
  if (bridge.refs > 0) return;
  bridge.unsubscribe();
  authBridges.delete(client);
}

class SharedTerminalRealtime {
  private readonly subscribers = new Set<TerminalRealtimeSubscriber>();
  private readonly releaseAuth: () => void;
  private channel: RealtimeChannel | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private connecting = false;
  private disposed = false;
  private state: TerminalTransportState = 'connecting';

  constructor(
    private readonly client: SupabaseClient,
    private readonly nodeId: string,
    private readonly onEmpty: () => void,
  ) {
    this.releaseAuth = retainRealtimeAuth(client);
    void this.connect();
  }

  add(subscriber: TerminalRealtimeSubscriber): TerminalRealtimeLease {
    this.subscribers.add(subscriber);
    subscriber.onTransport?.(this.state, this.reconnectAttempt);
    let released = false;
    return {
      send: (event, payload) => this.send(event, payload),
      recover: () => this.requestRecovery(),
      release: () => {
        if (released) return;
        released = true;
        this.subscribers.delete(subscriber);
        if (this.subscribers.size === 0) {
          this.dispose();
          this.onEmpty();
        }
      },
    };
  }

  private setState(state: TerminalTransportState): void {
    if (this.state === state) return;
    this.state = state;
    for (const subscriber of this.subscribers) subscriber.onTransport?.(state, this.reconnectAttempt);
  }

  private emit(event: TerminalRealtimeBroadcastEvent, payload: unknown): void {
    for (const subscriber of this.subscribers) subscriber.onBroadcast?.(event, payload);
  }

  private removeChannel(target: RealtimeChannel | null): void {
    if (!target) return;
    if (this.channel === target) this.channel = null;
    void this.client.removeChannel(target);
  }

  private clearRetry(): void {
    if (this.retryTimer === null) return;
    clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private enterFallback(target: RealtimeChannel | null = this.channel): void {
    if (this.disposed) return;
    this.removeChannel(target);
    this.setState('fallback');
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.disposed || this.retryTimer !== null || this.channel !== null) return;
    const delay = terminalReconnectDelay(this.reconnectAttempt);
    this.reconnectAttempt += 1;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.disposed || this.channel !== null || this.connecting) return;
      this.setState('reconnecting');
      void this.connect();
    }, delay);
  }

  private requestRecovery(): void {
    if (this.disposed || this.state === 'live') return;
    // The supervisor may request resubscribe, but the one shared transport keeps
    // ownership of timing so many Worker surfaces cannot create a reconnect storm.
    this.scheduleReconnect();
  }

  private async connect(): Promise<void> {
    if (this.disposed || this.connecting || this.channel !== null) return;
    this.connecting = true;
    try {
      const { data } = await this.client.auth.getSession();
      if (data.session?.access_token) await this.client.realtime.setAuth(data.session.access_token);
      if (this.disposed) return;

      // Supabase hands back a same-topic channel that is still leaving instead of a new one.
      // Binding the listeners onto it again made each frame arriving in that window emit
      // twice. Finish the earlier removal first; if it is still listed, retry on the backoff.
      const topic = `realtime:terminal:${this.nodeId}`;
      const leaving = this.client.getChannels().find((channel) => channel.topic === topic);
      if (leaving !== undefined) await this.client.removeChannel(leaving);
      if (this.disposed) return;
      if (this.client.getChannels().some((channel) => channel.topic === topic)) {
        this.enterFallback(null);
        return;
      }

      const next = this.client.channel(`terminal:${this.nodeId}`, {
        config: { private: true, broadcast: { ack: true, self: false } },
      });
      next.on('broadcast', { event: 'terminal-output' }, ({ payload }) => this.emit('terminal-output', payload));
      next.on('broadcast', { event: 'terminal-state' }, ({ payload }) => this.emit('terminal-state', payload));
      next.on('broadcast', { event: 'terminal-input-result' }, ({ payload }) => this.emit('terminal-input-result', payload));
      next.on('broadcast', { event: 'persistent-ai-state' }, ({ payload }) => this.emit('persistent-ai-state', payload));
      // #661: a Worker session's Actual AI changed in the database; re-read it there.
      next.on('broadcast', { event: 'ai-execution-actual' }, ({ payload }) => this.emit('ai-execution-actual', payload));
      this.channel = next;
      next.subscribe((status) => {
        if (this.disposed || this.channel !== next) return;
        if (status === 'SUBSCRIBED') {
          this.clearRetry();
          this.reconnectAttempt = 0;
          this.setState('live');
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          this.enterFallback(next);
        }
      });
    } catch {
      if (!this.disposed) this.enterFallback(null);
    } finally {
      this.connecting = false;
    }
  }

  async send(event: string, payload: unknown): Promise<boolean> {
    const target = this.channel;
    if (this.disposed || this.state !== 'live' || target === null) return false;
    try {
      const status = await target.send({ type: 'broadcast', event, payload });
      if (status === 'ok') return true;
    } catch {
      // A missing acknowledgement is ambiguous: the node may already have received it.
    }
    if (!this.disposed && this.channel === target) this.enterFallback(target);
    return false;
  }

  private dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.clearRetry();
    this.removeChannel(this.channel);
    this.releaseAuth();
  }
}

/**
 * All mounted Worker surfaces on the same computer share one private terminal channel.
 *
 * Supabase channel topics are node-scoped. Creating one same-topic channel per Worker caused
 * collapsed cards and the Conversation session listener to compete with the live terminal
 * attachment. This registry keeps one transport per node and fans broadcasts out locally.
 */
const terminalRealtimeByClient = new WeakMap<SupabaseClient, Map<string, SharedTerminalRealtime>>();

export function subscribeTerminalRealtime(
  client: SupabaseClient,
  nodeId: string,
  subscriber: TerminalRealtimeSubscriber,
): TerminalRealtimeLease {
  let byNode = terminalRealtimeByClient.get(client);
  if (!byNode) {
    byNode = new Map();
    terminalRealtimeByClient.set(client, byNode);
  }
  let shared = byNode.get(nodeId);
  if (!shared) {
    shared = new SharedTerminalRealtime(client, nodeId, () => {
      const current = terminalRealtimeByClient.get(client);
      current?.delete(nodeId);
      if (current?.size === 0) terminalRealtimeByClient.delete(client);
    });
    byNode.set(nodeId, shared);
  }
  return shared.add(subscriber);
}
